import type * as React from "react";
import { useState, useEffect } from "react";

// Third Party
import { useQueryClient, useQuery, useMutation } from "@tanstack/react-query";
import { useNavigate, createFileRoute } from "@tanstack/react-router";

// UI
import { useToast } from "@/components/ui/use-toast";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
	Card,
	CardHeader,
	CardTitle,
	CardDescription,
	CardContent,
} from "@/components/ui/card";
import { ArrowLeft, Save } from "lucide-react";
import {
	Table,
	TableBody,
	TableCell,
	TableHead,
	TableHeader,
	TableRow,
} from "@/components/ui/table";
import { FileUpload } from "@/components/FileUpload";
import { Spinner } from "@/components/ui/spinner";
import DatePicker from "@/components/ui/DatePicker";

// Services
import { getFixAssets, editFixAssets } from "@/services/finance";

// Utils
import formatDate from "@/utils/formatDate";
import type { FixAssetDetail } from "@/types/finance";

// ------------------ Helper Functions ------------------

function formatNumberWithCommas(value: string | number) {
	if (!value && value !== 0) return "";

	const stringValue = String(value || "");
	const isNegative = stringValue.startsWith("(") && stringValue.endsWith(")");
	const numericValue = isNegative ? stringValue.slice(1, -1) : stringValue;

	// Handle the case when user is typing a decimal point
	if (numericValue.endsWith('.')) {
		return isNegative ? `(${numericValue})` : numericValue;
	}

	// Split number into integer and decimal parts
	const [integerPart, decimalPart] = numericValue.split(".");

	// Format integer part with commas
	const formattedInteger = integerPart.replace(/\B(?=(\d{3})+(?!\d))/g, ",");

	// Handle decimal part - limit to 2 decimal places
	const formattedDecimal = decimalPart ? decimalPart.slice(0, 2) : undefined;

	// Combine with decimal part if it exists
	const formattedValue =
		formattedDecimal !== undefined
			? `${formattedInteger}.${formattedDecimal}`
			: formattedInteger;

	return isNegative ? `(${formattedValue})` : formattedValue;
}

function parseParenthesisNumber(strValue: string | number) {
	if (!strValue) return 0;
	const stringValue = String(strValue).replace(/,/g, ""); // remove commas
	const isNegative = stringValue.startsWith("(") && stringValue.endsWith(")");
	const numericValue = isNegative ? stringValue.slice(1, -1) : stringValue;

	const parsed = Number(numericValue) || 0;
	return isNegative ? -parsed : parsed;
}

function computeSumForField(data: any[], fieldName: string) { // eslint-disable-line @typescript-eslint/no-explicit-any
	const sum = data.reduce((acc, row) => {
		return acc + parseParenthesisNumber(row[fieldName]);
	}, 0);
	return sum.toLocaleString();
}

// ------------------ EditFixAssets Page ------------------

export const Route = createFileRoute(
	"/_layout-private/report/finance/company/_layout/fix-assets/edit",
)({
	component: EditFixAssets,
});

function EditFixAssets() {
	const { toast } = useToast();
	const navigate = useNavigate();
	const queryClient = useQueryClient();
	const companyId = localStorage.getItem("companyId");
	const year = Number(localStorage.getItem("year"));
	const [docFile, setDocFile] = useState("");

	// Fetch data from backend
	const { data, isLoading, error } = useQuery({
		queryKey: ["fix-assets", companyId, year],
		queryFn: () => getFixAssets(companyId as string, year),
		enabled: Boolean(companyId && year),
	});

	const mutation = useMutation({
		mutationFn: async (values: {
			docFile: string;
			fixAssetDetails: FixAssetDetail[];
		}) => {
			try {
				await editFixAssets(companyId as string, {
					companyId: companyId ?? undefined,
					yearly: year,
					docFile: values.docFile,
					fixAssetDetails: values.fixAssetDetails,
				});
			} catch (error) {
				console.error("Error submitting data:", error);
				throw error;
			}
		},
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: ["fix-assets", companyId, year] });
			navigate({ to: "/report/finance/company/fix-assets" });
			toast({
				title: "ສຳເລັດ",
				description: "ບັນທຶກຂໍ້ມູນສຳເລັດ",
				variant: "success",
			});
		},
		onError: () => {
			toast({
				title: "ບໍ່ສຳເລັດ",
				description: "ບັນທຶກຂໍ້ມູນບໍ່ສຳເລັດ",
				variant: "destructive",
			});
		},
	});

	// Initial state setup for fix assets
	const [fixAssets, setFixAssets] = useState<FixAssetDetail[]>([]);

	useEffect(() => {
		if (data?.data) {
			setDocFile(data.data.docFile);
			// setFixAssets(data.data.fixAssetDetails);

			const prepareDate = data.data.fixAssetDetails.map(
				(item: FixAssetDetail) => ({
					...item,
					receivedDate: item.receivedDate
						? formatDate({
							date: item.receivedDate,
							format: "YYYY-MM-DD",
						})
						: "",
					disposalDate: item.disposalDate
						? formatDate({
							date: item.disposalDate,
							format: "YYYY-MM-DD",
						})
						: "",
				}),
			);

			setFixAssets(prepareDate);
		}
	}, [data]);

	function handleNumericChange({
		e,
		index,
		fieldName,
		data,
		setData,
	}: {
		e: React.ChangeEvent<HTMLInputElement>;
		index: number;
		fieldName:
		| "count"
		| "originalValue"
		| "depreciationValue"
		| "remainingValue"
		| "disposalValue";
		data: any[]; // eslint-disable-line @typescript-eslint/no-explicit-any
		setData: React.Dispatch<React.SetStateAction<any[]>>; // eslint-disable-line @typescript-eslint/no-explicit-any
	}) {
		let value = e.target.value;

		// Allow empty input
		if (!value) {
			const newData = [...data];
			newData[index][fieldName] = "";
			setData(newData);
			return;
		}

		// Remove existing commas
		value = value.replace(/,/g, '');

		// Allow numbers, decimal point, and parentheses
		if (!/^[(]?\d*\.?\d*[)]?$/.test(value) && !/^[(]$/.test(value)) {
			return;
		}

		// Handle single opening parenthesis
		if (value === '(') {
			const newData = [...data];
			newData[index][fieldName] = value;
			setData(newData);
			return;
		}

		const newData = [...data];
		newData[index][fieldName] = value;
		setData(newData);
	}

	const handleSubmit = async (e: React.FormEvent) => {
		e.preventDefault();

		if (!companyId || !year) {
			toast({
				title: "ຂໍ້ມູນບໍ່ຄົບຖ້ວນ",
				description: "ກະລຸນາປ້ອນຂໍ້ມູນ",
				variant: "destructive",
			});
			return;
		}

		if (!docFile) {
			toast({
				title: "ບັນທຶກລົ້ມເຫຼວ",
				description: "ກະລຸນາອັບໂຫຼດເອກະສານຕິດຂັດ",
				variant: "destructive",
			});
			return;
		}

		const finalFixAssets = fixAssets.map((row) => ({
			...row,
			count: parseParenthesisNumber(row.count || 0),
			originalValue: parseParenthesisNumber(row.originalValue),
			depreciationValue: parseParenthesisNumber(row.depreciationValue),
			remainingValue: parseParenthesisNumber(
				row.originalValue - row.depreciationValue,
			),
			receivedDate: row.receivedDate || "",
			disposalValue: parseParenthesisNumber(row.disposalValue),
			disposalDate: row.disposalDate || "",
		}));

		const dataToSubmit = {
			companyId,
			yearly: year,
			docFile,
			fixAssetDetails: finalFixAssets,
		};

		mutation.mutate(dataToSubmit);
	};

	if (isLoading) return <Spinner />;
	if (error) return <div>Error loading data</div>;

	return (
		<section>
			<Card className="border-none">
				<CardHeader className="border-b mb-[32px]">
					<button
						type="button"
						onClick={() =>
							navigate({
								to: "/report/finance",
								search: {
									page: 1,
									startYear: 0,
									endYear: 0,
								},
							})
						}
						className="flex items-center gap-[4px] mb-4"
					>
						<ArrowLeft className="w-5 h-5" />
						ກັບຄືນ
					</button>
					<CardTitle>ແກ້ໄຂຂໍ້ມູນຊັບສິນຄົງທີ່</CardTitle>
					<CardDescription>ກະລຸນາປ້ອນຂໍ້ມູນຊັບສິນຄົງທີ່</CardDescription>
				</CardHeader>
				<CardContent>
					<form onSubmit={handleSubmit}>
						{/* Year */}
						<div className="flex flex-col gap-2 mb-4">
							<Label>ປະຈຳປີ</Label>
							<Input
								type="number"
								value={year}
								disabled
								className="max-w-[300px]"
							/>
						</div>

						{/* Fix Assets Table */}
						<Table className="overflow-x-auto w-full border table-fixed">
							<TableHeader>
								<TableRow className="[&>*]:border [&>*]:w-[180px]   [&>*]:font-bold [&>*]:text-black">
									<TableHead>ຊື່</TableHead>
									<TableHead>ຈໍານວນ</TableHead>
									<TableHead>ຫົວໜ່ວຍ</TableHead>
									<TableHead>ວ.ດ.ປ ຮັບເຂົ້າ</TableHead>
									<TableHead>
										ມູນຄ່າເດີມ <span className="text-red-500">*</span>
									</TableHead>
									<TableHead>
										ຄ່າຫຼຸ້ຍຫ້ຽນສະສົມ <span className="text-red-500">*</span>
									</TableHead>
									<TableHead>ມູນຄ່າຍັງເຫຼືອ</TableHead>
									<TableHead>ວັນທີສະສາງ</TableHead>
								</TableRow>
							</TableHeader>
							<TableBody>
								{fixAssets.map((asset, index) => (
									<TableRow className="[&>*]:border " key={index}>
										<TableCell>
											<Input
												type="text"
												value={asset.name}
												onChange={(e) => {
													const newAssets = [...fixAssets];
													newAssets[index].name = e.target.value;
													setFixAssets(newAssets);
												}}
												required
												readOnly
											/>
										</TableCell>
										<TableCell>
											<Input
												type="text"
												value={formatNumberWithCommas(asset.count)}
												onChange={(e) =>
													handleNumericChange({
														e,
														index,
														fieldName: "count",
														data: fixAssets,
														setData: setFixAssets,
													})
												}
												className="text-right"
												// required
												inputMode="numeric"
											/>
										</TableCell>
										<TableCell>
											<Input
												type="text"
												value={asset.unit}
												onChange={(e) => {
													const newAssets = [...fixAssets];
													newAssets[index].unit = e.target.value;
													setFixAssets(newAssets);
												}}
											/>
										</TableCell>
										<TableCell>
											<DatePicker
												showLabel={false}
												value={asset.receivedDate}
												onChange={(value) => {
													const newAssets = [...fixAssets];
													newAssets[index].receivedDate = value;
													setFixAssets(newAssets);
												}}
											// required
											/>
										</TableCell>
										<TableCell>
											<Input
												type="text"
												value={formatNumberWithCommas(asset.originalValue)}
												onChange={(e) =>
													handleNumericChange({
														e,
														index,
														fieldName: "originalValue",
														data: fixAssets,
														setData: setFixAssets,
													})
												}
												className="text-right"
												required
												inputMode="numeric"
											/>
										</TableCell>
										<TableCell>
											<Input
												type="text"
												value={formatNumberWithCommas(asset.depreciationValue)}
												onChange={(e) =>
													handleNumericChange({
														e,
														index,
														fieldName: "depreciationValue",
														data: fixAssets,
														setData: setFixAssets,
													})
												}
												className="text-right"
												required
												inputMode="numeric"
											/>
										</TableCell>
										<TableCell className="text-right">
											{formatNumberWithCommas(
												asset.originalValue - asset.depreciationValue,
											)}
										</TableCell>
										<TableCell>
											<Input
												type="date"
												value={asset.disposalDate}
												onChange={(e) => {
													const newAssets = [...fixAssets];
													newAssets[index].disposalDate = e.target.value;
													setFixAssets(newAssets);
												}}
											/>
										</TableCell>
										{/* <TableCell>
											<Button
												onClick={() => {
													const newAssets = fixAssets.filter(
														(_, i) => i !== index,
													);
													setFixAssets(newAssets);
												}}
												type="button"

											>
												<Trash2 className="text-red-500" />
											</Button>
										</TableCell> */}
									</TableRow>
								))}
								<TableRow className="bg-gray-100 [&>*]:border">
									<TableCell className="font-bold text-center">ລວມ</TableCell>
									<TableCell className="text-right">
										{computeSumForField(fixAssets, "count")}
									</TableCell>
									<TableCell></TableCell>
									<TableCell></TableCell>
									<TableCell className="text-right">
										{computeSumForField(fixAssets, "originalValue")}
									</TableCell>
									<TableCell className="text-right">
										{/* Sum of Depreciation Value */}

										{computeSumForField(fixAssets, "depreciationValue")}
									</TableCell>
									<TableCell className="text-right">
										{/* Sum of Remaining Value */}
										{formatNumberWithCommas(
											fixAssets.reduce(
												(acc, asset) =>
													acc +
													(parseParenthesisNumber(asset.originalValue) -
														parseParenthesisNumber(asset.depreciationValue)),
												0,
											),
										)}
									</TableCell>
									<TableCell className="text-right">
										{/* Sum of Disposal Value */}
										{computeSumForField(fixAssets, "disposalValue")}
									</TableCell>
									<TableCell></TableCell>
								</TableRow>
							</TableBody>
						</Table>

						{/* File Upload */}
						<div className="flex gap-x-1 items-center mt-4">
							<FileUpload
								currentFile={docFile}
								onFileUpload={(file) => setDocFile(file)}
							/>
						</div>

						{/* Submit */}
						<div className="flex gap-x-2 justify-end mt-4">
							<Button
								type="button"

								onClick={() =>
									navigate({
										to: "/report/finance",
										search: {
											page: 1,
											startYear: 0,
											endYear: 0,
										},
									})
								}
								variant={"outline"}
							>
								ຍົກເລີກ
							</Button>
							<Button
								disabled={mutation.isPending}
								type="submit"
								className="flex gap-x-2 items-center"
							>
								{mutation.isPending ? (
									<Spinner className="text-white" />
								) : (
									<>
										<Save className="w-4 h-4" />
										ບັນທຶກ
									</>
								)}
							</Button>
						</div>
					</form>
				</CardContent>
			</Card>
		</section>
	);
}

export default EditFixAssets;
